import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';

export class AuthenticationError extends Error {
  constructor(message, code = 'AUTH_ERROR') {
    super(message);
    this.name = 'AuthenticationError';
    this.code = code;
  }
}

function getUserPool() {
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

  if (!userPoolId || !clientId) {
    throw new AuthenticationError(
      'Cognito configuration missing. Check environment variables.',
      'CONFIG_MISSING'
    );
  }

  return new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });
}

function getCognitoUser(email) {
  return new CognitoUser({
    Username: email,
    Pool: getUserPool(),
  });
}

export function extractUserFromSession(session) {
  const idToken = session.getIdToken();
  const email = idToken.payload.email;
  const givenName = idToken.payload.given_name;
  const familyName = idToken.payload.family_name;

  // Calculate initials with fallback logic
  let initials;
  if (givenName && familyName) {
    // New users with full names: "Holly Baker" -> "HB"
    const firstInitial = givenName[0].toUpperCase();
    const lastInitial = familyName[0].toUpperCase();
    initials = `${firstInitial}${lastInitial}`;
  } else if (email) {
    // Legacy users without names: "4@example.com" -> "4"
    initials = email.split('@')[0][0].toUpperCase();
  } else {
    // Ultimate fallback for edge cases
    initials = 'U';
  }

  // Calculate full name with fallback
  const name = givenName && familyName
    ? `${givenName} ${familyName}`
    : email.split('@')[0];

  return {
    user: {
      email,
      firstName: givenName || null,
      lastName: familyName || null,
      name,
      initials,
      id: idToken.payload.sub,
      enabled_modules: idToken.payload['custom:enabled_modules'] || '',
    },
    tokens: {
      idToken: idToken.getJwtToken(),
      accessToken: session.getAccessToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
    },
  };
}

export function extractUserFromPayload(payload) {
  const email = payload.email || payload.sub;
  const givenName = payload.given_name;
  const familyName = payload.family_name;
  const name = givenName && familyName ? `${givenName} ${familyName}` : email.split('@')[0];
  const initials = givenName && familyName
    ? `${givenName[0].toUpperCase()}${familyName[0].toUpperCase()}`
    : (email[0] || 'U').toUpperCase();
  return {
    email,
    firstName: givenName || null,
    lastName: familyName || null,
    name,
    initials,
    id: payload.sub,
    enabled_modules: payload['custom:enabled_modules'] || '',
  };
}

export async function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = getCognitoUser(email);

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve(extractUserFromSession(session));
      },

      onFailure: (err) => {
        const errorMessages = {
          NotAuthorizedException: 'Incorrect email or password',
          UserNotFoundException: 'User not found',
          UserNotConfirmedException: 'Please verify your email',
          InvalidParameterException: 'Invalid email or password format',
        };

        const message = errorMessages[err.code] || err.message || 'Authentication failed';
        reject(new AuthenticationError(message, err.code));
      },

      newPasswordRequired: () => {
        reject(new AuthenticationError(
          'New password required. Contact support.',
          'NEW_PASSWORD_REQUIRED'
        ));
      },
    });
  });
}

export async function signOut() {
  const cognitoUser = getUserPool().getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

export async function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();

    if (!cognitoUser) {
      reject(new AuthenticationError('No current user', 'NO_USER'));
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) {
        reject(new AuthenticationError('Session expired', 'SESSION_EXPIRED'));
        return;
      }

      resolve(extractUserFromSession(session));
    });
  });
}

export async function forgotPassword(email) {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCognitoUser(email);

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve({ message: 'Password reset code sent to your email' });
      },
      onFailure: (err) => {
        reject(new AuthenticationError(
          err.message || 'Failed to send reset code',
          err.code
        ));
      },
    });
  });
}

// Used when a platform SSO handoff token expires (platform id tokens have 60-min TTL).
// TODO CC-302: wire call site in AuthContext — detect expiry and call this to re-issue tokens.
export async function refreshSession(email, refreshTokenStr) {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCognitoUser(email);
    const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenStr });
    cognitoUser.refreshSession(refreshToken, (err, session) => {
      if (err) {
        reject(new AuthenticationError(err.message || 'Refresh failed', err.code));
        return;
      }
      resolve(extractUserFromSession(session));
    });
  });
}

export async function confirmPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCognitoUser(email);

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve({ message: 'Password reset successful' });
      },
      onFailure: (err) => {
        const errorMessages = {
          CodeMismatchException: 'Invalid verification code',
          ExpiredCodeException: 'Code expired',
          InvalidPasswordException: 'Password does not meet requirements',
        };

        const message = errorMessages[err.code] || err.message || 'Password reset failed';
        reject(new AuthenticationError(message, err.code));
      },
    });
  });
}

/**
 * Refresh session using a platform pool refresh token.
 * Calls the PC API's /auth/refresh-token with pool_type=platform.
 */
export async function refreshPlatformSession(refreshToken) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const response = await fetch(`${apiUrl}/auth/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken, pool_type: 'platform' }),
  });

  if (!response.ok) {
    throw new Error('Platform session refresh failed');
  }

  const data = await response.json();
  const payload = JSON.parse(atob(data.id_token.split('.')[1]));
  const user = extractUserFromPayload(payload);
  return {
    user,
    tokens: { idToken: data.id_token, accessToken: data.access_token, refreshToken },
  };
}
